import React, { useEffect, useRef, useState } from 'react';
import type { AgentStatus } from '@/hooks/useMarimoSession';
import type { DemoUser } from '@/data/users';
import type { DatasetType, Project } from './MainLayout';

// Small reusable dropdown — replaces native <select> so the popup font
// matches the page (no OS-rendered list) and there's no dark-mode flash.
interface SelectOption<T extends string> {
  key: T;
  label: string;
}

interface SelectProps<T extends string> {
  options: SelectOption<T>[];
  value: T;
  onChange: (key: T) => void;
}

function Select<T extends string>({ options, value, onChange }: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.key === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={selStyles.wrapper}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={selStyles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={selStyles.triggerLabel}>{selected?.label ?? ''}</span>
        <span style={selStyles.chevron}>{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <ul role="listbox" style={selStyles.menu}>
          {options.map((o) => {
            const isSelected = o.key === value;
            return (
              <li
                key={o.key}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(o.key);
                  setOpen(false);
                }}
                style={{
                  ...selStyles.option,
                  ...(isSelected ? selStyles.optionSelected : {}),
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected ? '#dbeafe' : '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : 'white';
                }}
              >
                {o.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const selStyles: Record<string, React.CSSProperties> = {
  wrapper: { position: 'relative', width: '100%' },
  trigger: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    backgroundColor: 'white',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    padding: '0.5rem 0.65rem',
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#0f172a',
  },
  triggerLabel: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  chevron: { fontSize: '0.75rem', color: '#64748b', flexShrink: 0 },
  menu: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    margin: 0,
    padding: 0,
    listStyle: 'none',
    backgroundColor: 'white',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
    zIndex: 20,
    maxHeight: 280,
    overflowY: 'auto',
  },
  option: {
    padding: '0.5rem 0.65rem',
    cursor: 'pointer',
    backgroundColor: 'white',
    transition: 'background-color 0.1s',
    fontSize: '0.85rem',
    color: '#0f172a',
  },
  optionSelected: { backgroundColor: '#eff6ff', fontWeight: 600 },
};

interface SidebarProps {
  isConnected: boolean;
  agents: AgentStatus[];
  onClearChat: () => void;
  project: Project;
  onProjectChange: (p: Project) => void;
  datasetType: DatasetType;
  onDatasetTypeChange: (t: DatasetType) => void;
  activeUser: DemoUser;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isConnected,
  agents,
  onClearChat,
  project,
  onProjectChange,
  datasetType,
  onDatasetTypeChange,
  activeUser,
}) => {
  const projectOptions = activeUser.projects.map((p) => ({ key: p.key, label: p.label }));
  const activeProject =
    activeUser.projects.find((p) => p.key === project) ?? activeUser.projects[0];
  const typeOptions = activeProject ? activeProject.types : [];

  return (
    <aside style={styles.sidebar}>
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Agent Status</h3>
        {/* Orchestrator always shown; its reachability = backend up */}
        <div style={styles.agentRow}>
          <div
            style={{
              ...styles.statusDot,
              backgroundColor: isConnected ? '#22c55e' : '#ef4444',
            }}
          />
          <div>
            <div style={styles.agentName}>Orchestrator (Claude)</div>
            <div style={styles.agentSub}>{isConnected ? 'online' : 'offline'}</div>
          </div>
        </div>
        {agents.length === 0 && isConnected && (
          <div style={styles.empty}>Loading agents…</div>
        )}
        {agents.map((a) => {
          const color =
            a.status === 'online' ? '#22c55e' :
            a.status === 'no key' ? '#f59e0b' : '#ef4444';
          return (
            <div key={a.id} style={styles.agentRow}>
              <div style={{ ...styles.statusDot, backgroundColor: color }} />
              <div>
                <div style={styles.agentName}>{a.display_name}</div>
                <div style={styles.agentSub}>{a.status}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>User Profile</h3>
        <div style={styles.profileBox}>
          <div style={styles.profileName}>{activeUser.display_name}</div>
          <div style={styles.profileSub}>{activeUser.profile.description}</div>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Domain Agents</h3>
        {activeUser.agents.length ? (
          <ul style={styles.list}>
            {activeUser.agents.map((a) => (
              <li key={a.key} style={styles.listItem}>
                <div style={styles.itemTitle}>{a.display_name}</div>
                <div style={styles.itemSub}>{a.description}</div>
              </li>
            ))}
          </ul>
        ) : (
          <div style={styles.empty}>—</div>
        )}
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Project</h3>
        <Select
          options={projectOptions}
          value={project}
          onChange={(next) => {
            onProjectChange(next);
            // Reset dataset type to the first valid one for the new project
            const proj = activeUser.projects.find((p) => p.key === next);
            const first = proj?.types[0]?.key;
            if (first) onDatasetTypeChange(first);
          }}
        />
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Dataset Type</h3>
        <Select options={typeOptions} value={datasetType} onChange={onDatasetTypeChange} />
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Quick Actions</h3>
        <button
          onClick={onClearChat}
          style={styles.button}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
        >
          Clear Chat
        </button>
      </div>
    </aside>
  );
};

const styles: Record<string, React.CSSProperties> = {
  sidebar: {
    width: 280,
    backgroundColor: '#f8fafc',
    borderRight: '1px solid #e2e8f0',
    padding: '1.5rem',
    overflowY: 'auto',
  },
  section: { marginBottom: '1.75rem' },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#475569',
    marginBottom: '0.5rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  statusDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  agentRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  agentName: { fontSize: '0.82rem', fontWeight: 500, color: '#0f172a', lineHeight: 1.3 },
  agentSub: { fontSize: '0.7rem', color: '#64748b' },
  profileBox: {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    padding: '0.6rem 0.75rem',
  },
  profileName: { fontSize: '0.9rem', fontWeight: 600, color: '#0f172a' },
  profileSub: { fontSize: '0.75rem', color: '#64748b', marginTop: 2 },
  list: { listStyle: 'none', padding: 0, margin: 0 },
  listItem: {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    padding: '0.5rem 0.6rem',
    marginBottom: 6,
  },
  itemTitle: { fontSize: '0.85rem', fontWeight: 500, color: '#0f172a' },
  itemSub: { fontSize: '0.7rem', color: '#64748b', marginTop: 4 },
  empty: { fontSize: '0.8rem', color: '#94a3b8' },
  select: {
    width: '100%',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#0f172a',
    backgroundColor: 'white',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    padding: '0.5rem 0.65rem',
    cursor: 'pointer',
  },
  button: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    fontSize: '0.85rem',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};
